import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { IMAGES, BUSINESS, LOGO } from "../data/site";
import { useQuote } from "./QuoteModal";

const TRUST = ["Family Run", "Fully Insured", "Quality Workmanship", "Free Quotations"];

export default function Hero() {
  const { openQuote } = useQuote();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative h-[100svh] min-h-[680px] overflow-hidden grain" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Impressive detached British family home in Cheshire with a newly installed dark slate pitched roof by Sterling Roofworks"
          className="w-full h-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D]/60 via-[#0D0D0D]/20 to-[#0D0D0D]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D]/65 via-[#0D0D0D]/20 to-transparent" />
      </motion.div>

      <div className="relative z-10 container-lux h-full flex flex-col justify-center pt-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <img src={LOGO} alt="Sterling Roofworks" className="h-20 sm:h-24 mb-5 animate-floaty" />
        </motion.div>

        <div className="overline mb-3">
          <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }} className="inline-block">
            Roofers in Stockport, Greater Manchester &amp; Cheshire
          </motion.span>
        </div>

        <h1 className="font-head font-bold uppercase tracking-tighter leading-[1] text-5xl sm:text-6xl lg:text-7xl max-w-5xl">
          <motion.span
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="block text-silver-metallic pb-1">Premium Roofing.</motion.span>
          <motion.span
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="block text-gold-metallic pb-1">Built To Last.</motion.span>
        </h1>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-[#E8E8E8] leading-relaxed">
          Professional roofing solutions for homeowners across Stockport, Greater Manchester and Cheshire.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-9 flex flex-wrap gap-4">
          <button data-testid="hero-get-quote" onClick={openQuote} className="btn-gold rounded-sm px-7 py-4 text-sm">Get A Free Quote</button>
          <a data-testid="hero-view-work" href="#gallery" className="btn-ghost rounded-sm px-7 py-4 text-sm">View Our Work</a>
          <a data-testid="hero-call" href={BUSINESS.phoneHref} className="btn-ghost rounded-sm px-7 py-4 text-sm inline-flex items-center gap-2">
            <Phone size={16} strokeWidth={1.5} /> Call Now
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-x-6 gap-y-3" data-testid="hero-trust-indicators">
          {TRUST.map((b) => (
            <span key={b} className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-[#F8F8F8]/85 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />{b}
            </span>
          ))}
        </motion.div>
      </div>

      <motion.a href="#why" style={{ opacity }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-[#C8C8C8] flex flex-col items-center gap-2"
        animate={{ y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity }}>
        <span className="text-[10px] uppercase tracking-[0.25em]">Scroll</span>
        <ArrowDown size={18} strokeWidth={1.5} />
      </motion.a>
    </section>
  );
}
